<template>
  <transition name="ipd-fade">
    <div
      v-if="detailCard"
      ref="overlay"
      class="ipd-overlay"
      role="dialog"
      aria-modal="true"
      :aria-label="detailCard.title"
      tabindex="-1"
    >
      <div class="ipd-panel">
        <div class="ipd-preview">
          <!-- 图片类：成果图多图预览 -->
          <div v-if="isImageDetail && !detailLoading" class="ipd-image-preview">
            <div class="ipd-result-main" :style="imagePreviewBoxStyle">
              <img
                v-if="activeResultImage"
                :src="activeResultImage.url"
                :alt="activeResultImage.name || detailCard.title"
                class="ipd-result-main-img"
                @load="onDetailMediaMeta"
              />
              <div v-else class="ipd-result-empty">暂无成果图</div>
            </div>
            <div v-if="resultImages.length > 1" class="ipd-result-thumbs" role="tablist" aria-label="成果图列表">
              <button
                v-for="(img, index) in resultImages"
                :key="img.id"
                type="button"
                class="ipd-result-thumb"
                :class="{ active: activeResultIndex === index }"
                :aria-selected="activeResultIndex === index ? 'true' : 'false'"
                @click="activeResultIndex = index"
              >
                <img :src="img.url" :alt="img.name" loading="lazy" />
              </button>
            </div>
          </div>

          <!-- 视频类 / 加载中占位 -->
          <div v-else class="ipd-main-media" :style="detailMediaBoxStyle">
            <video
              v-if="detailCard.type === 'video' && !detailLoading"
              ref="detailVideo"
              class="ipd-main-video"
              controls
              playsinline
              preload="none"
              @loadedmetadata="onDetailMediaMeta"
              @play="onDetailVideoPlay"
              @ended="onDetailVideoEnded"
            ></video>
            <img
              v-else
              :src="detailCard.cover"
              :alt="detailCard.title"
              class="ipd-main-img"
              @load="onDetailMediaMeta"
            />
          </div>
        </div>

        <div class="ipd-info">
          <button class="ipd-close" type="button" @click="close" aria-label="关闭">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>

          <div class="ipd-info-scroll">
            <h2 class="ipd-title">{{ detailCard.title }}</h2>
            <p class="ipd-desc">{{ getCardMeta(detailCard).desc }}</p>
            <span class="ipd-ai-tag">
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
                <path d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"/>
              </svg>
              内容由 AI 生成
            </span>

            <div class="ipd-section" v-if="displayScenes.length">
              <p class="ipd-section-label">适用场景</p>
              <div class="ipd-tags">
                <span
                  v-for="scene in displayScenes"
                  :key="scene"
                  class="ipd-tag"
                >{{ scene }}</span>
              </div>
            </div>

            <!-- 图片类：参数信息 -->
            <template v-if="isImageDetail">
              <div v-if="imageParamRows.length" class="ipd-section">
                <p class="ipd-section-label">参数信息</p>
                <dl class="ipd-param-list">
                  <div v-for="row in imageParamRows" :key="row.label" class="ipd-param-row">
                    <dt>{{ row.label }}</dt>
                    <dd>{{ row.value }}</dd>
                  </div>
                </dl>
              </div>

              <div v-if="imagePrompt" class="ipd-section">
                <p class="ipd-section-label">需求描述</p>
                <pre class="ipd-prompt">{{ imagePrompt }}</pre>
              </div>

              <div class="ipd-section">
                <p class="ipd-section-label">参考图</p>
                <div v-if="productImages.length" class="ipd-samples ipd-samples--refs">
                  <div
                    v-for="item in productImages"
                    :key="item.id"
                    class="ipd-sample ipd-sample--image"
                  >
                    <img :src="item.url" :alt="item.name" loading="lazy" />
                  </div>
                </div>
                <p v-else class="ipd-samples-empty">暂无参考图</p>
              </div>
            </template>

            <!-- 视频类：案例素材 -->
            <div v-else class="ipd-section">
              <p class="ipd-section-label">当前案例素材：</p>
              <div v-if="detailMaterials.length" class="ipd-samples">
                <div
                  v-for="item in detailMaterials"
                  :key="item.id"
                  class="ipd-sample"
                  :class="`ipd-sample--${item.type}`"
                >
                  <img
                    v-if="item.type === 'image'"
                    :src="item.url"
                    :alt="item.name"
                    loading="lazy"
                  />
                  <video
                    v-else-if="item.type === 'video'"
                    :ref="`sample-vid-${item.id}`"
                    controls
                    playsinline
                    muted
                    preload="none"
                    :title="item.name"
                    @mouseenter="loadSampleVideo(item)"
                  ></video>
                  <div v-else class="ipd-sample-audio">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true">
                      <path d="M9 18V5l12-2v13"/><circle cx="6" cy="18" r="3"/><circle cx="18" cy="16" r="3"/>
                    </svg>
                    <audio :src="item.url" controls preload="metadata" :title="item.name"></audio>
                    <span class="ipd-sample-audio-name">{{ item.name }}</span>
                  </div>
                </div>
              </div>
              <p v-else class="ipd-samples-empty">暂无素材</p>
            </div>
          </div>

          <div class="ipd-actions">
            <button type="button" class="ipd-remake-btn" @click="handleSameStyle">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" aria-hidden="true">
                <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
              </svg>
              一键复刻
            </button>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import { getInspirationDetail } from '@/api/inspiration'
import {
  isInspirationApiSuccess,
  isInspirationImageCase,
  mergeInspirationDetailCard,
  parseInspirationScenes,
} from '@/utils/inspirationMappers'
import { requireLogin } from '@/utils/authGuard'

const CATEGORY_DESC_MAP = {
  'Seedance2.0': '全球首款同时支持首尾帧和音频的旗舰视频模型',
  '达人种草': '达人拓展化种草，自然演绎促转化',
  '创意特效': '高级创意特效生成，视觉冲击力强，适合品牌发布与短视频包装',
  '商品主图': '为商品量身打造，一键解锁高级质感商业大片',
  '营销海报': '节日氛围感爆棚，一键生成高质感宣传海报',
  '社媒口播': '社媒口播内容生成，适合短视频投放与品牌传播',
}

export default {
  name: 'InspirationDetailModal',

  props: {
    categories: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      detailCard: null,
      detailMaterials: [],
      detailMediaRatio: null,
      detailLoading: false,
      detailVideoPlayed: false,
      currentTabId: '',
      activeResultIndex: 0,
    }
  },

  computed: {
    isImageDetail() {
      if (!this.detailCard || this.detailCard.type !== 'image') return false
      return isInspirationImageCase(this.detailCard.paramInfo, this.detailCard.type)
        || !!(this.detailCard.resultImages && this.detailCard.resultImages.length)
        || !!(this.detailCard.imageParamSummary)
    },
    resultImages() {
      if (!this.detailCard || !Array.isArray(this.detailCard.resultImages)) return []
      return this.detailCard.resultImages
    },
    activeResultImage() {
      return this.resultImages[this.activeResultIndex] || this.resultImages[0] || null
    },
    productImages() {
      if (!this.detailCard || !Array.isArray(this.detailCard.productImages)) return []
      return this.detailCard.productImages
    },
    imageParamRows() {
      return this.detailCard?.imageParamSummary?.rows || []
    },
    imagePrompt() {
      return String(this.detailCard?.imageParamSummary?.prompt || '').trim()
    },
    displayScenes() {
      if (!this.detailCard) return []
      const scenes = (this.detailCard.scenes && this.detailCard.scenes.length)
        ? this.detailCard.scenes
        : parseInspirationScenes(this.detailCard.paramInfo)
      if (scenes.length) return scenes
      return Array.isArray(this.detailCard.categoryNames)
        ? this.detailCard.categoryNames.filter(Boolean)
        : []
    },
    detailMediaBoxStyle() {
      const ratio = this.detailMediaRatio || this.getDetailAspectRatio(this.detailCard)
      if (!ratio) return {}
      return { aspectRatio: String(ratio) }
    },
    imagePreviewBoxStyle() {
      const ratio = this.detailCard?.imageParamSummary?.aspectRatioValue
        || this.detailMediaRatio
        || this.getDetailAspectRatio(this.detailCard)
      if (!ratio) return {}
      return { aspectRatio: String(ratio) }
    },
  },

  beforeDestroy() {
    this.stopDetailVideo()
    this.clearSampleVideos()
    if (this.detailCard) {
      document.body.style.overflow = ''
    }
  },

  methods: {

    async open(card, tabId = '') {
      if (!card || card.id == null) return
      this.stopDetailVideo()
      this.clearSampleVideos()
      this.currentTabId = String(tabId || card.tabId || '').trim()
      this.detailVideoPlayed = false
      this.detailCard = { ...card, tabId: card.tabId || this.currentTabId }
      this.detailMaterials = []
      this.detailMediaRatio = null
      this.activeResultIndex = 0
      document.body.style.overflow = 'hidden'
      this.detailLoading = true
      try {
        const res = await getInspirationDetail({ id: card.id })
        if (isInspirationApiSuccess(res) && res.data) {
          this.detailVideoPlayed = false
          this.detailCard = mergeInspirationDetailCard(
            this.detailCard,
            res.data,
            this.detailCard.tabId || this.currentTabId,
          )
          this.detailMaterials = Array.isArray(this.detailCard.generalMaterials)
            ? this.detailCard.generalMaterials
            : []
          this.activeResultIndex = 0
          const summaryRatio = this.detailCard.imageParamSummary?.aspectRatioValue
          if (summaryRatio) {
            this.detailMediaRatio = summaryRatio
          } else {
            this.detailMediaRatio = this.getDetailAspectRatio(this.detailCard)
          }
        }
      } catch (err) {
        console.warn('[InspirationDetailModal] fetchDetail failed', err)
      } finally {
        this.detailLoading = false
      }
      this.$nextTick(() => {
        this.initDetailVideo()
        const panel = this.$refs.overlay
        if (panel) panel.focus()
      })
    },

    close() {
      this.stopDetailVideo()
      this.clearSampleVideos()
      this.detailCard = null
      this.detailMaterials = []
      this.detailMediaRatio = null
      this.detailVideoPlayed = false
      this.currentTabId = ''
      this.activeResultIndex = 0
      document.body.style.overflow = ''
      this.$emit('close')
    },

    handleSameStyle() {
      if (!this.detailCard) return
      if (!requireLogin(this, '请先登录后使用一键复刻功能')) return
      const card = this.detailCard
      const tabId = this.detailCard.tabId || this.currentTabId
      this.close()
      this.$emit('same-style', card, tabId)
    },

    getDetailMediaUrl() {
      if (!this.detailCard || this.detailCard.type !== 'video') return ''
      return String(this.detailCard.playUrl || '').trim()
    },

    getCategoryByTabId(tabId) {
      if (!tabId || tabId === 'discover') return null
      return this.categories.find((c) => String(c.id) === String(tabId)) || null
    },

    getCategoryDesc(name) {
      const key = String(name || '').trim()
      return CATEGORY_DESC_MAP[key] || '通过 AI 技术生成的高质量创意内容，一键复刻同款风格。'
    },

    getCardMeta(card) {
      const tabId = card.tabId || this.currentTabId
      const cat = this.getCategoryByTabId(tabId)
      const catName = String(cat?.name || '').trim()
      const scenes = (card.scenes && card.scenes.length)
        ? card.scenes
        : parseInspirationScenes(card.paramInfo)
      const desc = String(card.description || '').trim() || this.getCategoryDesc(catName)
      return { desc, scenes }
    },

    getDetailAspectRatio(card) {
      if (!card) return 9 / 16
      const summaryRatio = card.imageParamSummary?.aspectRatioValue
      if (summaryRatio) return summaryRatio
      const map = {
        tall: 9 / 16,
        portrait: 3 / 4,
        square: 1,
        landscape: 16 / 9,
      }
      if (card.ratio && map[card.ratio]) return map[card.ratio]
      return 214 / 350
    },

    getDetailVideoEl() {
      const ref = this.$refs.detailVideo
      return Array.isArray(ref) ? ref[0] : ref
    },

    getSampleVideoEl(item) {
      if (!item || item.id == null) return null
      const ref = this.$refs[`sample-vid-${item.id}`]
      return Array.isArray(ref) ? ref[0] : ref
    },

    initDetailVideo() {
      if (!this.detailCard || this.detailCard.type !== 'video') return
      const url = this.getDetailMediaUrl()
      if (!url) return
      const vid = this.getDetailVideoEl()
      if (!vid) return

      if (vid._detailPlayHandler) {
        vid.removeEventListener('canplay', vid._detailPlayHandler)
        vid._detailPlayHandler = null
      }

      if (vid.dataset.loadedUrl === url) {
        this.tryAutoplayDetailVideo()
        return
      }

      vid.dataset.loadedUrl = url
      vid.src = url
      vid.load()

      const tryPlay = () => {
        if (!vid.src || vid.dataset.loadedUrl !== url || this.detailVideoPlayed) return
        vid.muted = false
        vid.volume = 1
        vid.loop = false
        const playPromise = vid.play()
        if (playPromise && typeof playPromise.then === 'function') {
          playPromise
            .then(() => {
              this.detailVideoPlayed = true
            })
            .catch(() => {})
        } else {
          this.detailVideoPlayed = true
        }
      }

      if (vid.readyState >= 3) {
        tryPlay()
      } else {
        vid._detailPlayHandler = tryPlay
        vid.addEventListener('canplay', vid._detailPlayHandler, { once: true })
      }
    },

    loadSampleVideo(item) {
      const url = String(item?.url || '').trim()
      if (!url) return
      const vid = this.getSampleVideoEl(item)
      if (!vid) return
      if (vid.dataset.loadedUrl === url) return
      vid.dataset.loadedUrl = url
      vid.src = url
      vid.load()
    },

    clearSampleVideos() {
      if (!this.detailMaterials.length) return
      this.detailMaterials.forEach((item) => {
        if (item.type !== 'video') return
        const vid = this.getSampleVideoEl(item)
        if (!vid) return
        vid.pause()
        vid.removeAttribute('src')
        delete vid.dataset.loadedUrl
        vid.load()
      })
    },

    tryAutoplayDetailVideo() {
      if (this.detailLoading || this.detailVideoPlayed) return
      if (!this.detailCard || this.detailCard.type !== 'video') return
      const vid = this.getDetailVideoEl()
      if (!vid || !vid.src) return
      vid.muted = false
      vid.volume = 1
      vid.loop = false
      const playPromise = vid.play()
      if (playPromise && typeof playPromise.then === 'function') {
        playPromise
          .then(() => {
            this.detailVideoPlayed = true
          })
          .catch(() => {})
      } else {
        this.detailVideoPlayed = true
      }
    },

    onDetailVideoPlay(e) {
      const vid = e?.target || this.getDetailVideoEl()
      if (!vid) return
      if (vid.ended) {
        vid.currentTime = 0
      }
      vid.muted = false
      vid.volume = 1
    },

    onDetailVideoEnded(e) {
      const vid = e?.target || this.getDetailVideoEl()
      if (!vid) return
      vid.pause()
    },

    stopDetailVideo() {
      const vid = this.getDetailVideoEl()
      if (!vid) return
      if (vid._detailPlayHandler) {
        vid.removeEventListener('canplay', vid._detailPlayHandler)
        vid._detailPlayHandler = null
      }
      vid.pause()
      vid.removeAttribute('src')
      delete vid.dataset.loadedUrl
      vid.load()
      this.detailVideoPlayed = false
    },

    pauseDetailVideo() {
      this.stopDetailVideo()
    },

    onDetailMediaMeta(e) {
      const el = e.target
      const w = el.videoWidth || el.naturalWidth || 0
      const h = el.videoHeight || el.naturalHeight || 0
      if (w > 0 && h > 0) {
        this.detailMediaRatio = w / h
      }
    },
  },
}
</script>

<style scoped>
.ipd-overlay {
  position: fixed;
  inset: 0;
  z-index: 2000;
  background: rgba(0, 0, 0, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  outline: none;
}

.ipd-panel {
  display: flex;
  width: min(96vw, 1480px);
  max-width: min(96vw, 1480px);
  height: min(calc(100vh - 32px), 900px);
  border-radius: 14px;
  overflow: hidden;
  background: #0f0f14;
  box-shadow: 0 32px 80px rgba(0, 0, 0, 0.7);
}

.ipd-preview {
  flex: 1;
  min-width: 0;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px 24px;
  background: #000;
}

.ipd-image-preview {
  width: 100%;
  height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.ipd-result-main {
  flex: 1;
  min-height: 0;
  width: 100%;
  max-width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  overflow: hidden;
  background: #0a0a0a;
}

.ipd-result-main-img {
  display: block;
  width: 100%;
  height: 100%;
  max-height: 100%;
  object-fit: contain;
}

.ipd-result-empty {
  color: rgba(255, 255, 255, 0.35);
  font-size: 13px;
}

.ipd-result-thumbs {
  flex-shrink: 0;
  display: flex;
  justify-content: center;
  gap: 8px;
  overflow-x: auto;
  padding-bottom: 2px;
  scrollbar-width: thin;
  scrollbar-color: rgba(255,255,255,0.15) transparent;
}
.ipd-result-thumbs::-webkit-scrollbar { height: 4px; }
.ipd-result-thumbs::-webkit-scrollbar-thumb {
  background: rgba(255,255,255,0.15);
  border-radius: 2px;
}

.ipd-result-thumb {
  flex-shrink: 0;
  width: 56px;
  height: 56px;
  padding: 0;
  border-radius: 6px;
  overflow: hidden;
  border: 2px solid transparent;
  background: rgba(255, 255, 255, 0.06);
  cursor: pointer;
  transition: border-color 0.18s, opacity 0.18s;
}
.ipd-result-thumb.active {
  border-color: #84cc16;
}
.ipd-result-thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.ipd-main-media {
  position: relative;
  height: 100%;
  max-height: 100%;
  max-width: 100%;
  width: auto;
  flex-shrink: 0;
  overflow: hidden;
  border-radius: 8px;
  background: #000;
}

.ipd-main-video,
.ipd-main-img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: contain;
  background: #000;
}

.ipd-info {
  width: 400px;
  flex-shrink: 0;
  background: #14141c;
  display: flex;
  flex-direction: column;
  position: relative;
  border-left: 1px solid rgba(255, 255, 255, 0.07);
}

.ipd-close {
  position: absolute;
  top: 14px;
  right: 14px;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  border: none;
  background: rgba(255, 255, 255, 0.08);
  color: rgba(255, 255, 255, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.18s, color 0.18s;
  z-index: 5;
}
.ipd-close:hover {
  background: rgba(255, 255, 255, 0.16);
  color: #fff;
}

.ipd-info-scroll {
  flex: 1;
  overflow-y: auto;
  padding: 20px 20px 12px;
  scrollbar-width: thin;
  scrollbar-color: rgba(255,255,255,0.12) transparent;
}
.ipd-info-scroll::-webkit-scrollbar { width: 4px; }
.ipd-info-scroll::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.12); border-radius: 2px; }

.ipd-title {
  font-size: 18px;
  font-weight: 700;
  color: #f0f0f5;
  margin: 0 32px 10px 0;
  line-height: 1.3;
}

.ipd-desc {
  font-size: 12.5px;
  color: rgba(255, 255, 255, 0.45);
  line-height: 1.65;
  margin: 0 0 12px;
}

.ipd-ai-tag {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  color: rgba(255, 255, 255, 0.35);
  margin-bottom: 18px;
}

.ipd-section {
  margin-bottom: 20px;
}

.ipd-section-label {
  font-size: 11.5px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.35);
  text-transform: uppercase;
  letter-spacing: 0.06em;
  margin: 0 0 10px;
}

.ipd-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 7px;
}

.ipd-tag {
  padding: 4px 11px;
  border-radius: 100px;
  font-size: 12px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.75);
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.1);
  cursor: default;
  white-space: nowrap;
}

.ipd-samples {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 5px;
}

.ipd-sample {
  aspect-ratio: 1 / 1;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: rgba(255, 255, 255, 0.04);
}
.ipd-sample img,
.ipd-sample video {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.ipd-sample--audio,
.ipd-sample-audio {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 8px;
  height: 100%;
  color: rgba(255, 255, 255, 0.7);
}

.ipd-sample-audio audio {
  width: 100%;
  height: 28px;
}

.ipd-sample-audio-name {
  width: 100%;
  font-size: 11px;
  line-height: 1.3;
  text-align: center;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: rgba(255, 255, 255, 0.55);
}

.ipd-samples-empty {
  margin: 0;
  padding: 10px 0 4px;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.45);
}

.ipd-param-list {
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.ipd-param-row {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  font-size: 12.5px;
  line-height: 1.5;
}

.ipd-param-row dt {
  flex-shrink: 0;
  width: 68px;
  margin: 0;
  color: rgba(255, 255, 255, 0.38);
}

.ipd-param-row dd {
  margin: 0;
  flex: 1;
  color: rgba(255, 255, 255, 0.82);
  word-break: break-word;
}

.ipd-prompt {
  margin: 0;
  padding: 10px 12px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.07);
  font-family: inherit;
  font-size: 12px;
  line-height: 1.65;
  color: rgba(255, 255, 255, 0.72);
  white-space: pre-wrap;
  word-break: break-word;
  max-height: 180px;
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: rgba(255,255,255,0.12) transparent;
}

.ipd-samples--refs {
  grid-template-columns: repeat(3, 1fr);
}

.ipd-actions {
  padding: 14px 20px;
  border-top: 1px solid rgba(255, 255, 255, 0.07);
  flex-shrink: 0;
}

.ipd-remake-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  width: 100%;
  height: 44px;
  border-radius: 10px;
  border: none;
  background: #84cc16;
  color: #1a2200;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
  letter-spacing: 0.02em;
  transition: background 0.2s, transform 0.15s, box-shadow 0.2s;
  box-shadow: 0 4px 16px rgba(132, 204, 22, 0.35);
}
.ipd-remake-btn:hover {
  background: #a3e635;
  transform: translateY(-1px);
  box-shadow: 0 6px 24px rgba(132, 204, 22, 0.5);
}
.ipd-remake-btn:active {
  transform: translateY(0);
}

.ipd-fade-enter-active,
.ipd-fade-leave-active {
  transition: opacity 0.22s ease;
}
.ipd-fade-enter-active .ipd-panel,
.ipd-fade-leave-active .ipd-panel {
  transition: transform 0.22s ease, opacity 0.22s ease;
}
.ipd-fade-enter,
.ipd-fade-leave-to {
  opacity: 0;
}
.ipd-fade-enter .ipd-panel,
.ipd-fade-leave-to .ipd-panel {
  transform: scale(0.96);
  opacity: 0;
}

@media (max-width: 900px) {
  .ipd-panel {
    flex-direction: column;
    width: min(96vw, 100%);
    max-width: min(96vw, 100%);
    height: calc(100vh - 24px);
  }
  .ipd-preview {
    flex: 1 1 auto;
    min-height: 0;
    padding: 12px 16px;
  }
  .ipd-image-preview {
    gap: 8px;
  }
  .ipd-result-thumb {
    width: 48px;
    height: 48px;
  }
  .ipd-main-media {
    max-width: 100%;
  }
  .ipd-info {
    width: 100%;
    max-height: 42vh;
    flex-shrink: 0;
  }
}
@media (max-width: 600px) {
  .ipd-overlay { padding: 0; }
  .ipd-panel {
    width: 100%;
    max-width: 100%;
    height: 100dvh;
    border-radius: 0;
  }
  .ipd-samples { grid-template-columns: repeat(4, 1fr); }
}
</style>
